import { format } from 'date-fns'
import { Fragment, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import type { CheckpointResponseResponse } from '@/api/client'
import {
  useCreateCheckpointResponses,
  useUpdateCheckpointResponses,
  useWorkOrderCheckpointResponses,
} from '@/api/hooks/useCheckpointResponses'
import { useUsersByWorkshop } from '@/api/hooks/useUsersByWorkshop'
import { useWorkOrder } from '@/api/hooks/useWorkOrder'
import { useWorkOrderErrands, type WorkOrderErrand } from '@/api/hooks/useWorkOrderErrands'
import LicensePlate from '@/components/molecules/license-plate'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogScrollable,
  DialogTitle,
} from '@/components/ui/dialog'
import { appConfig } from '@/config/app.config'
import {
  buildInitialAnswers,
  type Checkpoint,
  type FlatAnswer,
  flattenResponses,
  getAllQuestions,
  getQuestionsToAsk,
} from '@/utils/checkpoint.utils'
import { CheckpointQuestionField } from './checkpoint-question'
import {
  getDevDefaultAnswer,
  isAnswerComplete,
  type QuestionAnswer,
} from './checkpoint-question.utils'
import { uploadImageFiles } from './upload-images'

type Props = {
  open: boolean
  onOpenChange(open: boolean): void
  workOrderId: string
  workflowStateId: string
  workshopId: string
  workOrderType?: string | null
  checkpoint: Checkpoint
  currentUserId: string
  registrationNumber?: string
  existingResponse?: CheckpointResponseResponse
  onComplete(): void
}

const emptyAnswer = (): QuestionAnswer => ({ value: [], files: [] })

export const CheckpointDialog = ({
  open,
  onOpenChange,
  workOrderId,
  workflowStateId,
  workshopId,
  workOrderType,
  checkpoint,
  currentUserId,
  registrationNumber,
  existingResponse,
  onComplete,
}: Props) => {
  const { t } = useTranslation()
  const { mutateAsync: createResponses, isPending: isCreating } = useCreateCheckpointResponses()
  const { mutateAsync: updateResponses, isPending: isUpdating } = useUpdateCheckpointResponses()
  const isPending = isCreating || isUpdating
  const [answers, setAnswers] = useState<Map<string, QuestionAnswer>>(new Map())

  useEffect(() => {
    if (!open) return

    if (existingResponse) {
      const seeded = new Map<string, QuestionAnswer>()
      for (const [questionId, existing] of Array.from(
        buildInitialAnswers(getAllQuestions(checkpoint), [existingResponse]),
      )) {
        seeded.set(questionId, {
          value: existing.value,
          files: [],
          comment: existing.comment,
          existingImages: existing.images,
        })
      }
      setAnswers(seeded)
      return
    }

    const initial = new Map<string, QuestionAnswer>()
    if (!appConfig.isProduction) {
      for (const question of getAllQuestions(checkpoint)) {
        if (!question.id) continue
        const defaultAnswer = getDevDefaultAnswer(question)
        if (defaultAnswer) initial.set(question.id, defaultAnswer)
      }
    }
    setAnswers(initial)
  }, [open, checkpoint, existingResponse])

  const updateAnswer = (questionId: string, answer: QuestionAnswer) => {
    setAnswers(prev => new Map(prev).set(questionId, answer))
  }

  const visibleQuestions = useMemo(() => {
    const inSessionAnswers: FlatAnswer[] = Array.from(answers.entries()).map(([questionId, a]) => ({
      questionId,
      value: a.value,
      images: [],
      user: currentUserId,
      createdAt: '',
      checkpointId: checkpoint.id ?? '',
    }))
    return getQuestionsToAsk(checkpoint, [], currentUserId, {
      additionalTriggerAnswers: inSessionAnswers,
      workOrderType,
    })
  }, [checkpoint, currentUserId, answers, workOrderType])

  // Drop answers to questions a trigger change has hidden. It must compare
  // against the same render's `answers`: `visibleQuestions` is derived from
  // `answers`, so a functional updater here would judge freshly-seeded answers
  // (the before-enter-exit exit phase) against the visibility of the render
  // before seeding — which excludes every triggered question — and wipe them.
  useEffect(() => {
    const visibleIds = new Set(visibleQuestions.map(q => q.question.id))
    const hiddenIds = Array.from(answers.keys()).filter(id => !visibleIds.has(id))
    if (hiddenIds.length === 0) return

    const next = new Map(answers)
    for (const id of hiddenIds) next.delete(id)
    setAnswers(next)
  }, [visibleQuestions, answers])

  // `showWorkOrderNote` gates the whole context panel below (note, errands, and
  // comments left on other checkpoints), not just the work order note itself.
  const needsContextPanel = useMemo(
    () => visibleQuestions.some(({ question }) => question.showWorkOrderNote),
    [visibleQuestions],
  )
  const needsSpareParts = useMemo(
    () => visibleQuestions.some(({ question }) => question.type === 'spare-parts'),
    [visibleQuestions],
  )
  const { data: workOrder } = useWorkOrder(workOrderId, {
    enabled: open && (needsContextPanel || needsSpareParts),
  })
  const workOrderNote = workOrder?.note?.trim()
  const spareParts = workOrder?.spareParts ?? []

  const { data: errands } = useWorkOrderErrands(workOrderId, {
    enabled: open && needsContextPanel,
  })
  const { data: checkpointResponses } = useWorkOrderCheckpointResponses(workOrderId, {
    enabled: open && needsContextPanel,
  })
  const { data: workshopUsers } = useUsersByWorkshop(workshopId, {
    enabled: open && needsContextPanel,
  })
  const userName = (userId: string) => workshopUsers?.find(u => u.id === userId)?.name ?? userId
  // Errands grouped by type, newest first within each group, groups ordered
  // by their most recent errand.
  const errandGroups = useMemo(() => {
    const sorted = [...errands].sort(
      (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
    )

    const groups = new Map<string, { type: WorkOrderErrand['type']; errands: WorkOrderErrand[] }>()
    for (const errand of sorted) {
      const group = groups.get(errand.type)
      if (group) group.errands.push(errand)
      else groups.set(errand.type, { type: errand.type, errands: [errand] })
    }
    return Array.from(groups.values())
  }, [errands])
  // Every comment left on any checkpoint question for this work order (this
  // checkpoint's past submissions included), grouped by checkpoint, each
  // group's comments newest first, groups ordered by their most recent comment.
  const checkpointCommentGroups = useMemo(() => {
    const commented = flattenResponses(checkpointResponses)
      .filter(a => !!a.comment?.trim())
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())

    const groups = new Map<string, { checkpointName: string; comments: FlatAnswer[] }>()
    for (const a of commented) {
      const group = groups.get(a.checkpointId)
      if (group) group.comments.push(a)
      else
        groups.set(a.checkpointId, {
          checkpointName: a.checkpointName || a.checkpointId,
          comments: [a],
        })
    }
    return Array.from(groups.entries()).map(([checkpointId, group]) => ({ checkpointId, ...group }))
  }, [checkpointResponses])

  const canSubmit = useMemo(
    () =>
      visibleQuestions.every(({ question }) =>
        isAnswerComplete(
          question,
          answers.get(question.id ?? '') ?? emptyAnswer(),
          spareParts.length,
        ),
      ),
    [visibleQuestions, answers, spareParts.length],
  )

  const handleSubmit = async () => {
    try {
      const answersPayload = await Promise.all(
        visibleQuestions.map(async ({ question }) => {
          if (question.type === 'group') return null
          const answer = answers.get(question.id ?? '')
          if (!answer) return null

          const uploaded =
            question.type === 'images' && answer.files.length > 0
              ? await uploadImageFiles(workOrderId, answer.files)
              : []

          const images =
            question.type === 'images'
              ? [...(answer.existingImages ?? []), ...uploaded].map(img => ({
                  path: img.path,
                  name: img.name ?? undefined,
                }))
              : undefined

          return {
            questionId: question.id!,
            questionText: question.text,
            questionType: question.type,
            answerScope: question.answerScope ?? undefined,
            // Snapshotted like the other question config so readers (the General tab's
            // mileage photos) don't need the workflow to interpret an answer.
            contentLink:
              question.type === 'images' ? (question.contentLink ?? undefined) : undefined,
            value: answer.value,
            images,
            comment: answer.comment || undefined,
          }
        }),
      )

      const submittedAnswers = answersPayload.filter(data => !!data)

      if (existingResponse) {
        await updateResponses({
          path: { id: existingResponse.id },
          body: { answers: submittedAnswers },
        })
      } else {
        await createResponses({
          body: {
            workOrder: workOrderId,
            workflowState: workflowStateId,
            workshop: workshopId,
            checkpoint: { checkpointId: checkpoint.id!, name: checkpoint.name },
            answers: submittedAnswers,
          },
        })
      }

      onComplete()
    } catch (err) {
      toast.error(t('errors.error_save-answer'))
      console.error(err)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange} modal>
      <DialogContent
        size="ultrawide"
        anchor="top"
        className="max-h-[90vh] flex flex-col"
        onPointerDownOutside={e => e.preventDefault()}
        onInteractOutside={e => e.preventDefault()}
      >
        <DialogHeader>
          <div className="flex flex-col gap-4">
            <DialogTitle>{checkpoint.name}</DialogTitle>
            {registrationNumber && <LicensePlate registrationNumber={registrationNumber} />}
          </div>
          <DialogDescription className="sr-only">
            {t('checkpointDialog.description')}
          </DialogDescription>
          {!appConfig.isProduction && (
            <p className="text-sm font-medium text-error px-1">
              {t('checkpointDialog.devDefaultsWarning')}
            </p>
          )}
        </DialogHeader>

        <DialogScrollable className="flex-1 max-h-none overflow-y-auto px-1">
          {visibleQuestions.map(({ question }) => (
            <Fragment key={question.id}>
              <CheckpointQuestionField
                question={question}
                answer={answers.get(question.id ?? '') ?? emptyAnswer()}
                onChange={a => updateAnswer(question.id ?? '', a)}
                spareParts={spareParts}
                workshopId={workshopId}
              />
              {question.showWorkOrderNote && (
                <div className="mt-2 flex flex-col gap-2">
                  {workOrderNote && (
                    <div className="rounded-lg bg-field-2 border border-subtle-01 p-3">
                      <span className="block text-xs font-semibold text-text-secondary mb-1">
                        {t('checkpointQuestion.workOrderNoteLabel')}
                      </span>
                      <p className="text-sm whitespace-pre-wrap">{workOrderNote}</p>
                    </div>
                  )}
                  {errandGroups.length > 0 && (
                    <div className="rounded-lg bg-field-2 border border-subtle-01 p-3">
                      <span className="block text-xs font-semibold text-text-secondary mb-1">
                        {t('checkpointQuestion.errandsLabel')}
                      </span>
                      <div className="flex flex-col gap-4">
                        {errandGroups.map(group => (
                          <div key={group.type}>
                            <span className="block text-sm font-medium mb-1">
                              {t(`workOrderErrands.type.${group.type}` as const)}
                            </span>
                            <ul className="flex flex-col gap-3 pl-3 border-l border-subtle-01">
                              {group.errands.map(errand => (
                                <li key={errand.id} className="text-sm">
                                  <span className="font-medium">{errand.title}</span>
                                  {errand.message && (
                                    <p className="text-text-secondary whitespace-pre-wrap">
                                      {errand.message}
                                    </p>
                                  )}
                                  <span className="block text-xs text-text-secondary mt-0.5">
                                    {userName(errand.createdBy)} ·{' '}
                                    {format(new Date(errand.createdAt), 'd MMM yyyy HH:mm')}
                                  </span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {checkpointCommentGroups.length > 0 && (
                    <div className="rounded-lg bg-field-2 border border-subtle-01 p-3">
                      <span className="block text-xs font-semibold text-text-secondary mb-1">
                        {t('checkpointQuestion.checkpointCommentsLabel')}
                      </span>
                      <div className="flex flex-col gap-4">
                        {checkpointCommentGroups.map(group => (
                          <div key={group.checkpointId}>
                            <span className="block text-sm font-medium mb-1">
                              {group.checkpointName}
                            </span>
                            <ul className="flex flex-col gap-3 pl-3 border-l border-subtle-01">
                              {group.comments.map(a => (
                                <li key={`${a.questionId}-${a.createdAt}`} className="text-sm">
                                  {a.questionText && (
                                    <span className="block text-xs text-text-secondary">
                                      {a.questionText}
                                    </span>
                                  )}
                                  <p className="whitespace-pre-wrap">{a.comment}</p>
                                  <span className="block text-xs text-text-secondary mt-0.5">
                                    {userName(a.user)} ·{' '}
                                    {format(new Date(a.createdAt), 'd MMM yyyy HH:mm')}
                                  </span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </Fragment>
          ))}
        </DialogScrollable>

        <DialogFooter className="gap-2">
          <div className="flex-1" />
          <Button onClick={handleSubmit} disabled={!canSubmit || isPending}>
            {isPending
              ? t('checkpointDialog.submittingButton')
              : t('checkpointDialog.submitButton')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
